import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { ArrowRight, AlertCircle, RefreshCw } from "react-feather";
import { Spinner } from "reactstrap";

export const DEFAULT_MAX_ROWS = 6;

export const getVisibleApplicants = (applicants, maxRows = DEFAULT_MAX_ROWS) =>
  (applicants || []).slice(0, maxRows);

const getInitials = (name) => {
  if (!name) return "RD";
  const parts = name.replace(/,(.*)/, "").trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const RecentApplicants = ({ applicants, isLoading, error, onRetry, maxRows = DEFAULT_MAX_ROWS }) => {
  const history = useHistory();

  const handleApplicantClick = (applicant) => {
    if (applicant.campaign_id) {
      history.push(`/brands/campaign-applications/${applicant.campaign_id}`);
    } else if (applicant.rd_id) {
      history.push(`/dashboard/brands/nutritions/${applicant.rd_id}`);
    } else {
      history.push("/brands/outreach-posts");
    }
  };

  const applicantList = applicants || [];
  const visibleApplicants = applicantList.slice(0, maxRows);

  return (
    <div className="figma-split-card" id="brand-recent-applicants-card">
      <div className="figma-section-label mb-2">RECENT APPLICANTS</div>

      <div className="applicants-container-list">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-4" style={{ minHeight: "140px" }}>
            <Spinner color="primary" />
          </div>
        ) : error && applicantList.length === 0 ? (
          <div className="text-center py-3">
            <AlertCircle size={22} className="text-danger mb-50" />
            <p className="text-danger font-weight-bold mb-25 font-small-3">Failed to load applicants</p>
            <p className="text-muted font-small-2 mb-75">
              {typeof error === "string" ? error : error?.message || "Could not retrieve recent applicants."}
            </p>
            {onRetry && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                onClick={onRetry}
              >
                <RefreshCw size={12} className="mr-50" />
                <span>Retry</span>
              </button>
            )}
          </div>
        ) : applicantList.length === 0 ? (
          <div className="text-center py-3">
            <p className="text-muted mb-0 font-small-3">No applicants yet.</p>
            <small className="text-muted">When dietitians apply to your outreach posts, they'll appear here.</small>
          </div>
        ) : (
          <>
            {visibleApplicants.map((app, index) => {
              const isLast = index === visibleApplicants.length - 1;
              const rdName = app.rd_name || app.name || "Dietitian";
              const initials = app.initials || getInitials(rdName);
              const subtitle = app.campaign_title || app.specialties || "Campaign Applicant";

              return (
                <div
                  className={`figma-applicant-row ${!isLast ? "has-divider" : ""}`}
                  key={`applicant_${app.campaign_id || "c"}_${app.rd_id || "r"}_${index}`}
                >
                  <div className="applicant-left-profile">
                    {app.rd_avatar_url ? (
                      <img
                        src={app.rd_avatar_url}
                        alt={rdName}
                        className="applicant-dark-avatar rounded-circle"
                        style={{ objectFit: "cover" }}
                      />
                    ) : (
                      <div className="applicant-dark-avatar">
                        {initials}
                      </div>
                    )}

                    <div className="applicant-meta-details">
                      <div className="applicant-name-text d-flex align-items-center">
                        <span>{rdName}</span>
                        {app.is_viewed === false && (
                          <span className="badge badge-light-primary ml-50 font-small-1 py-0 px-25">
                            New
                          </span>
                        )}
                      </div>
                      <div className="applicant-specialty-sub">{subtitle}</div>
                    </div>
                  </div>

                  <div className="applicant-right-cta">
                    <button
                      type="button"
                      className="btn btn-link p-0 figma-view-link"
                      onClick={() => handleApplicantClick(app)}
                    >
                      <span className="view-link-text">{app.action_label || "View"}</span>
                      <ArrowRight size={14} className="ml-25 view-link-arrow" />
                    </button>
                  </div>
                </div>
              );
            })}

          </>
        )}
      </div>
    </div>
  );
};

RecentApplicants.propTypes = {
  applicants: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  onRetry: PropTypes.func,
  maxRows: PropTypes.number
};

RecentApplicants.defaultProps = {
  applicants: [],
  maxRows: DEFAULT_MAX_ROWS
};

export default RecentApplicants;
